import React, { useState, useEffect } from 'react'
import styles from './styles.module.scss'
import { Modal, ModalOverlay, ModalContent, ModalBody, VStack, Text, Button, Input, Box, Icon } from '@chakra-ui/react'
import { MdEmail, MdCheckCircle } from 'react-icons/md'

interface EmailVerificationModalProps {
  isOpen: boolean
  onClose: () => void
  onSkip?: () => void
  onVerify?: (otp: string) => void
  onResend?: () => void
  isLoading?: boolean
  deadlineDate?: string
  isPastDeadline?: boolean
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
  onSkip,
  onVerify,
  onResend,
  isLoading = false,
  deadlineDate,
  isPastDeadline = false
}) => {
  const [otp, setOtp] = useState('')
  const [resendCountdown, setResendCountdown] = useState(300)

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(prev => prev - 1)
      }, 1000)
    }
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [resendCountdown])

  // Start countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setResendCountdown(300)
    }
  }, [isOpen])

  const handleVerify = () => {
    if (onVerify) {
      onVerify(otp)
    }
  }

  const handleResend = () => {
    if (onResend && resendCountdown === 0) {
      onResend()
      setResendCountdown(300)
    }
  }

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6)
    setOtp(numericValue)
  }

  const getResendButtonText = () => {
    if (isLoading) return 'Sending...'
    if (resendCountdown > 0) {
      const minutes = Math.floor(resendCountdown / 60)
      const seconds = resendCountdown % 60
      if (minutes > 0) {
        return `Resend in ${minutes}m ${seconds}s`
      }
      return `Resend in ${resendCountdown}s`
    }
    return 'Resend'
  }

  return (
    <Modal isOpen={isOpen} onClose={() => {}} closeOnOverlayClick={false} closeOnEsc={false} isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxW="400px" borderRadius="16px" p={6} mx={4}>
        <ModalBody p={0}>
          <VStack spacing={2} align="center">
            <Box position="relative" mb={12} mt={8}>
              <Box
                w="100px"
                h="80px"
                bgGradient="linear(135deg, #FFA726 0%, #FF9800 100%)"
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                boxShadow="0 4px 12px rgba(255, 152, 0, 0.3)"
              >
                <Icon as={MdEmail} w={10} h={10} color="white" />
              </Box>
              <Box
                position="absolute"
                bottom="-10px"
                right="-10px"
                w="36px"
                h="36px"
                bg="green.500"
                borderRadius="50%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                border="4px solid white"
                boxShadow="0 2px 8px rgba(0, 0, 0, 0.15)"
              >
                <Icon as={MdCheckCircle} w={5} h={5} color="white" />
              </Box>
            </Box>

            <Text fontSize="20px" fontWeight="600" color="gray.800" textAlign="center">
              Check your email
            </Text>
            <Text fontSize="14px" color="gray.600" textAlign="center" lineHeight="1.5" px={2}>
              We've sent a One-Time Password to your email. Please check your inbox and follow the instructions to
              complete Single Sign-On authentication
            </Text>

            {/* One Time Password Input */}
            <Input
              data-bwignore="true"
              data-lpignore="true"
              data-1p-ignore
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Enter One-Time Password"
              value={otp}
              onChange={handleOtpChange}
              className={styles.otpInput}
              fontSize="16px"
              h="40px"
              borderRadius="8px"
              borderColor="gray.300"
              autoComplete="off"
              data-form-type="other"
              _focus={{
                borderColor: 'primary.500',
                boxShadow: '0 0 0 1px #00A9EB'
              }}
              _placeholder={{
                color: 'gray.400'
              }}
            />

            {/* Buttons */}
            <VStack spacing={4} w="100%">
              <Button
                w="100%"
                h="40px"
                bg="#00A9EB"
                color="white"
                borderRadius="12px"
                border={'none'}
                fontSize="16px"
                fontWeight="600"
                _hover={{ bg: '#0088CC' }}
                _active={{ bg: '#006699' }}
                onClick={handleVerify}
                isDisabled={otp.length !== 6 || isLoading}
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </Button>

              <Button
                h="40px"
                variant="outline"
                color="gray.600"
                fontSize="16px"
                fontWeight="500"
                _hover={{ bg: 'gray.50' }}
                border={'none'}
                borderRadius="12px"
                backgroundColor={'white'}
                onClick={handleResend}
                isDisabled={isLoading || resendCountdown > 0}
              >
                {getResendButtonText()}
              </Button>

              {/* Skip button - only show if not past deadline */}
              {!isPastDeadline && onSkip && (
                <Button
                  h="40px"
                  variant="outline"
                  color="gray.600"
                  fontSize="16px"
                  fontWeight="500"
                  _hover={{ bg: 'gray.50' }}
                  onClick={onSkip}
                  border={'none'}
                  borderRadius="12px"
                  backgroundColor={'white'}
                  isDisabled={isLoading}
                >
                  Skip
                </Button>
              )}
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default EmailVerificationModal
